"use client";

/**
 * Reusable share helper. Strategy:
 *   1. Use the Web Share API when available (mobile + recent desktop).
 *      Falls through to clipboard on `AbortError` (user cancelled) — that
 *      one is silent. Real errors still fall through to clipboard so the
 *      user always gets a working share path.
 *   2. Fall back to `navigator.clipboard.writeText(url)`.
 *   3. Last-resort fallback for very old browsers: a hidden textarea +
 *      `document.execCommand('copy')`. This keeps the button useful on
 *      legacy desktop or non-secure contexts where the Clipboard API is
 *      not available.
 *
 * Shows a toast on success/failure so the user gets feedback either way.
 */
import { toast } from "@/hooks/use-toast";

export interface SharePayload {
  title: string;
  text?: string;
  url: string;
  /** Optional remote image URL. Browsers that support `files` in
   *  Web Share Level 2 will share the image too. Silently skipped
   *  if unsupported or if fetching the image fails. */
  imageUrl?: string;
}

export async function share(payload: SharePayload): Promise<"shared" | "copied" | "failed"> {
  const { title, text, url, imageUrl } = payload;

  // Try Web Share API with files when supported.
  if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
    try {
      let files: File[] | undefined;
      if (imageUrl && typeof navigator.canShare === "function") {
        try {
          const res = await fetch(imageUrl);
          if (res.ok) {
            const blob = await res.blob();
            const filename = imageUrl.split("/").pop()?.split("?")[0] || "image.jpg";
            const file = new File([blob], filename, { type: blob.type || "image/jpeg" });
            if (navigator.canShare({ files: [file] })) files = [file];
          }
        } catch {
          // ignore image errors, share text+url anyway
        }
      }
      await navigator.share(files ? { title, text, url, files } : { title, text, url });
      return "shared";
    } catch (err: unknown) {
      // AbortError = user dismissed; don't show a toast and don't copy.
      if (err instanceof Error && err.name === "AbortError") return "shared";
      // Other errors fall through to clipboard.
    }
  }

  // Clipboard fallback.
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      toast({ title: "Enlace copiado", description: url, variant: "success", duration: 2500 });
      return "copied";
    }
  } catch {
    // continue to legacy fallback
  }

  // Legacy fallback for non-secure contexts / very old browsers.
  if (typeof document !== "undefined") {
    try {
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (ok) {
        toast({ title: "Enlace copiado", description: url, variant: "success", duration: 2500 });
        return "copied";
      }
    } catch {
      // fall through
    }
  }

  toast({ title: "No se pudo compartir", description: "Copia el enlace manualmente.", variant: "destructive" });
  return "failed";
}
